import { useMsal } from "@azure/msal-react";
import { Fragment, useRef } from "react";
import { Dialog, Transition, TransitionChild, DialogPanel, DialogTitle } from "@headlessui/react";
import DOMPurify from "dompurify";
import { marked, MarkedOptions } from "marked";

interface Props {
    open: boolean;
    setOpen: (open: boolean) => void;
}

export const Disclaimer = ({ open, setOpen }: Props) => {
    const cancelButtonRef = useRef(null);

    const { instance } = useMsal();
    const handleLogOutPopup = () => {
        instance
            .logoutPopup({
                mainWindowRedirectUri: "/", // redirects the top level app after logout
                account: instance.getActiveAccount(),
                logoutHint: instance.getActiveAccount()?.username // skips the account picker
            })
            .catch(error => console.log(error));
    };

    const onClose = () => {
        setOpen(false);
    };

    let disclaimerText = `AskEllie will help you find information from Equiom’s policies and procedures. As an AI, its replies are tailored to your conversation, offering a useful starting point for you to find answers to your questions.   
When using AskEllie, you should consider the following: 
- AskEllie is not a substitute for thoroughly reading and understanding full guidance, policies and procedures.  While we strive to ensure the responses are accurate and incorporate recent updates to policies, errors or outdated information may occasionally occur.  You should independently verify any critical information before taking any action based on responses. 
- Complex enquiries or those that need specific answers should be referred to the relevant colleagues - your HR Business Partner, for example.
- Anonymised content of conversations may be used to help refine prompts, improve accuracy, and expand the AskEllie knowledge base.
- Conversations with AskEllie are retained in Equiom’s chat history database and will automatically be deleted after 90 days.
`;

    const options: MarkedOptions = { async: false };
    const parsedDisclaimerText = marked.parse(disclaimerText, options) as string;

    return (
        <Transition show={open} as={Fragment}>
            <Dialog className="relative z-[100]" initialFocus={cancelButtonRef} onClose={() => undefined}>
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </TransitionChild>

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full justify-center p-4 text-center items-center sm:p-0">
                        <TransitionChild
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <DialogPanel className="relative transform overflow-hidden bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-xl">
                                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="ml-0 sm:mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-eq-light-grey sm:mx-0 sm:h-10 sm:w-10">
                                            <span className="material-symbols-outlined text-[1.5rem] text-black">warning</span>
                                        </div>
                                        <div className="mt-3 sm:ml-4 sm:mt-0 text-left">
                                            <DialogTitle as="h3" className="text-xl font-thin leading-6 text-gray-900">
                                                Disclaimer
                                            </DialogTitle>
                                            <div className="chat-stream mt-2">
                                                <p
                                                    className="text-sm text-gray-500"
                                                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(parsedDisclaimerText) }}
                                                ></p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="px-4 pb-6 flex flex-row sm:flex-row-reverse sm:px-6 gap-4">
                                    <a className="eq-primary-button m-0 w-[115px]" onClick={onClose}>
                                        <span className="">
                                            <span>Accept</span>
                                        </span>
                                    </a>
                                    <a className="eq-secondary-button m-0 w-[115px]" onClick={handleLogOutPopup}>
                                        <span className="">
                                            <span>Log out</span>
                                        </span>
                                    </a>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
